import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Product } from "../../types/Generic";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Modal from "../common/CustomModal";
import { Link } from "react-router-dom";
import { TbPhoneCall } from "react-icons/tb";
import { MdFileDownload } from "react-icons/md";
import deliveryIcon from "../../assets/fast-delivery.svg";
import { useTranslation } from "react-i18next";

interface ProductDetailProps {
  product: Product | undefined;
}

// const formatKey = (key: string) => {
//   return key
//     .replace(/([A-Z])/g, " $1")
//     .replace(/^./, (str) => str.toUpperCase());
// };

const toSnakeCase = (str: string) =>
  str.replace(/([A-Z])/g, "_$1").toLowerCase();

const ProductDetail: React.FC<ProductDetailProps> = ({ product }) => {
  const [selected, setSelected] = useState<number | null>(0);
  const [isHovered, setIsHovered] = useState(false);
  const [imageClicked, setImageClicked] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState<string | undefined>(
    product?.images[0],
  );

  const thumbnailRef = useRef<HTMLDivElement>(null);
  const thumbnailItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { t } = useTranslation();
  useEffect(() => {
    if (product && product.images.length > 0) {
      setActiveImage(product.images[0]);
    }
  }, [product]);

  useEffect(() => {
    const canAnimate = !!(product && product.images.length && !isHovered);
    const intervalId: NodeJS.Timeout | null = canAnimate
      ? setInterval(() => {
          setActiveImage((prev) => {
            const currentIndex = product.images.indexOf(prev!);
            const nextIndex = (currentIndex + 1) % product.images.length;
            return product.images[nextIndex];
          });
        }, 5000)
      : null;

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [product, isHovered, activeImage]);

  const toggle = (index: number) => {
    setSelected(selected === index ? null : index);
  };

  if (!product) {
    return <div>No Product Available at the moment</div>;
  }

  const scrollLeft = () => {
    if (thumbnailRef.current) {
      thumbnailRef.current.scrollBy({ left: -150, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (thumbnailRef.current) {
      thumbnailRef.current.scrollBy({ left: 150, behavior: "smooth" });
    }
  };

  return (
    <div className="flex flex-col gap-8 py-5 text-black sm:py-16 sm:w-4/5">
      <div className="gap-8 pb-8 md:flex">
        <div className="pb-8 md:w-1/2">
          <h2 className="mb-5 font-bold leading-tight text-[28px] ss:text-md md:max-w-[599px] tracking-wide">
            {t(product.title)}
          </h2>
          <div className="relative flex flex-col w-full rounded-lg h-max bg-[#FFF] border">
            <motion.img
              key={activeImage}
              src={activeImage}
              onClick={() => setImageClicked(activeImage ?? null)}
              alt={product.title}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="w-full h-[359px] rounded-lg object-fill cursor-pointer"
              initial={{ x: "10%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ duration: 0.5 }}
            />
          </div>

          <div className="relative w-full">
            <div className={`${product.images.length <= 3 ? "hidden" : ""}`}>
              <ArrowLeftIcon
                onClick={scrollLeft}
                sx={{ ":hover": { cursor: "pointer" }, color: "#000" }}
                className={`hidden absolute -left-4 top-1/2 -translate-y-1/2 bg-white rounded-full shadow-md z-10`}
              />
            </div>

            <div
              ref={thumbnailRef}
              className="flex gap-4 my-4 overflow-x-auto no-scrollbar scroll-smooth"
            >
              {product.images.map((img, idx) => (
                <div
                  id="thumbnail-item"
                  key={idx}
                  ref={(el) => {
                    thumbnailItemRefs.current[idx] = el;
                  }}
                  className={`max-w-[159px] min-w-[100px] flex justify-center items-center h-[80px] w-full rounded-md border bg-[#F7F8F9] cursor-pointer`}
                  onClick={() => setActiveImage(img)}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${idx}`}
                    className={`w-full h-[80px] object-contain border rounded-md transition-all duration-300 ${
                      activeImage === img
                        ? "opacity-100 border-2 border-primary shadow-sm"
                        : "opacity-50 hover:opacity-100"
                    }`}
                  />
                </div>
              ))}
            </div>

            <div className={`${product.images.length <= 3 ? "hidden" : ""}`}>
              <ArrowRightIcon
                onClick={scrollRight}
                sx={{ ":hover": { cursor: "pointer" }, color: "#000" }}
                className="absolute z-10 -translate-y-1/2 bg-white rounded-full shadow-md -right-4 top-1/2"
              />
            </div>
          </div>

          <div className="flex items-center w-full gap-8 mt-8">
            {product.pdfLInk && (
              <a
                href={product.pdfLInk}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 transition-all duration-300 bg-gray-100 text-[14px] font-medium border-2 rounded hover:opacity-75 hover:border-primary shadow-sm group"
              >
                <MdFileDownload className="group-hover:text-primary group-hover:shadow-md" />
                {t("fields.technical_card")}
              </a>
            )}

            {/* {product.price && ( */}
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-2">
                <img
                  src={deliveryIcon}
                  alt="fast-delivery"
                  className="object-contain w-10 h-10"
                />

                <span className="text-[14px] font-medium">
                  {t("fields.available_with_fast_delivery")}
                </span>
              </div>

              <a
                href="tel:+37061413680"
                className="flex items-center w-max gap-2 px-3 py-2 transition-all duration-300 bg-gray-100 text-[14px] font-medium border-2 rounded hover:opacity-75 hover:border-primary shadow-sm group"
              >
                <TbPhoneCall className="group-hover:text-primary" />
                {t("fields.lets_talk")}
              </a>
            </div>
            {/* )} */}
          </div>
        </div>

        <div className="space-y-5 md:w-1/2">
          {product &&
            Object.entries(product)
              .filter(
                ([key]) =>
                  ![
                    "title",
                    "price",
                    "pdfLInk",
                    "images",
                    "partnersReference",
                  ].includes(key),
              )
              .map(([key, value], index) => (
                <div key={index}>
                  <div
                    className="flex items-center justify-between py-3 cursor-pointer border-y-2"
                    onClick={() => toggle(index)}
                  >
                    <h3 className="font-semibold capitalize sm:text-sm">
                      {t(`fields.${key}`)}
                    </h3>

                    {selected === index ? (
                      <RemoveIcon className="border-2 border-black rounded-full" />
                    ) : (
                      <AddIcon className="border-2 border-black rounded-full" />
                    )}
                  </div>
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: selected === index ? "auto" : 0 }}
                    className="overflow-hidden"
                    transition={{ duration: 0.3 }}
                  >
                    <div className="p-5 bg-gray-100">
                      {key === "description" ? (
                        <p className="sm:text-[20px]">{t(value)}</p>
                      ) : key === "specification" &&
                        typeof value === "object" ? (
                        Object.keys(value).length > 0 ? (
                          <ul className="space-y-2">
                            {Object.entries(value).map(
                              ([specKey, specValue]) => (
                                <li key={specKey} className="sm:text-[18px]">
                                  <strong className="capitalize">
                                    {t(`fields.${toSnakeCase(specKey)}`)}:
                                  </strong>{" "}
                                  {String(specValue)}
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <p className="sm:text-[20px]">
                            No information available.
                          </p>
                        )
                      ) : value ? (
                        <p className="sm:text-[20px]">{value}</p>
                      ) : (
                        <p className="sm:text-[20px]">
                          No information available.xhr
                        </p>
                      )}
                    </div>
                  </motion.div>
                </div>
              ))}

          {product?.partnersReference && (
            <div>
              <h3 className="font-semibold capitalize sm:text-sm">
                {t("fields.partners_reference")}
              </h3>
              <div className="flex flex-wrap items-center gap-8 p-5 mt-2 bg-gray-100">
                {product.partnersReference.map((partner, idx) => (
                  <Link
                    key={idx}
                    target="_blank"
                    to={partner.path}
                    className="transition-transform duration-300 transform hover:scale-105 "
                  >
                    <img
                      src={partner.logo}
                      alt="Partner Logo"
                      className="object-contain w-[56px] h-auto"
                    />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal isOpen={!!imageClicked} onClose={() => setImageClicked(null)}>
        <img
          src={imageClicked!}
          className="object-contain max-h-[80vh] w-auto max-w-full mx-auto"
          alt={product.title}
        />
      </Modal>
    </div>
  );
};

export default ProductDetail;
